import { useCallback, useEffect, useState } from 'react'

import { CustomEvent } from '../../../enums/customEvent'
import { useCustomEventListener } from '../../../hooks/useCustomEventListener'
import { useSystemColorScheme } from '../../../hooks/useSystemColorScheme'
import { EmberEventProps } from '../../../types/emberEventProps'
import { ThemeLayout } from '../../../types/themeLayout'
import { ThemeLayoutMode } from '../../../types/themeLayoutMode'
import { dispatchCustomEvent } from '../../../utils/customEvent'
import { getGlobalBillyThemeLayoutMode } from '../../../utils/globalBilly'

export const useThemeLayout = () => {
  const { systemColorScheme } = useSystemColorScheme()

  /* Read synchronously (not in an effect) for a correct mount. Specifically the issue about ember <=> react bridge */
  const [themeLayoutMode, setThemeLayoutMode] = useState<ThemeLayoutMode | undefined>(
    () => getGlobalBillyThemeLayoutMode() ?? undefined,
  )
  const [themeLayout, setThemeLayout] = useState<ThemeLayout | undefined>()

  const dispatchThemeLayoutChanged = useCallback((themeLayout: ThemeLayout | undefined) => {
    if (themeLayout) {
      dispatchCustomEvent<ThemeLayout>(CustomEvent.ThemeLayoutChanged, themeLayout)
    }
  }, [])

  // initialization
  useEffect(
    () => {
      const initialThemeLayoutMode = getGlobalBillyThemeLayoutMode()

      if (initialThemeLayoutMode) {
        const initialThemeLayout = initialThemeLayoutMode === 'auto' ? systemColorScheme : initialThemeLayoutMode
        setThemeLayoutMode(initialThemeLayoutMode)
        setThemeLayout(initialThemeLayout)
        dispatchThemeLayoutChanged(initialThemeLayout)
      } else {
        const isDarkScheme = Boolean(window?.matchMedia?.('(prefers-color-scheme: dark)').matches)
        const fallbackTheme = isDarkScheme ? 'dark' : 'light'
        setThemeLayout(fallbackTheme)
        dispatchThemeLayoutChanged(fallbackTheme)
      }
    },
    // Warning disabled during the eslint warning cleanup. When refactoring this code fix this properly if possible.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  // observe system color scheme
  useEffect(() => {
    if (systemColorScheme === undefined) {
      return
    }

    if (themeLayoutMode === 'auto' || themeLayoutMode === undefined) {
      setThemeLayout(systemColorScheme)
      dispatchThemeLayoutChanged(systemColorScheme)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [systemColorScheme])

  const handleThemeLayoutModeChanged = useCallback(
    (event: EmberEventProps<ThemeLayoutMode>) => {
      const themeLayoutMode = event.detail
      const themeLayout = themeLayoutMode === 'auto' ? systemColorScheme : themeLayoutMode

      setThemeLayoutMode(themeLayoutMode)
      setThemeLayout(themeLayout)
      dispatchThemeLayoutChanged(themeLayout)
    },
    [dispatchThemeLayoutChanged, systemColorScheme],
  )

  useCustomEventListener<ThemeLayoutMode>(CustomEvent.ThemeLayoutModeChanged, handleThemeLayoutModeChanged)

  return { themeLayout }
}
